import axios, {AxiosError} from 'axios';
import Swal from 'sweetalert2';
import {ResponseList} from "../../../models/Response";
import {useState} from "react";


type EmailConfigModel = {
    sid: string
    wftype: string
    wfcondition: string
    contentsubject: string
    contentbody: string
    emailcc: string
    emailbcc: string
    showjsonreport: string
}

type EditForm = {
    wftype: string;
    contentsubject: string;
    contentbody: string;
    emailcc: string;
    emailbcc: string;
    showjsonreport: boolean;
}

const emptyEditForm = (): EditForm => ({
    wftype: '',
    contentsubject: '',
    contentbody: '',
    emailcc: '',
    emailbcc: '',
    showjsonreport: false
});

type CreateForm = {
    sid: string;
    wftype: string;
    wfcondition: string;
    contentsubject: string;
    contentbody: string;
    emailcc: string;
    emailbcc: string;
    showjsonreport: boolean;
}

const emptyCreateForm = (): CreateForm => ({
    sid: '',
    wftype: '',
    wfcondition: '',
    contentsubject: '',
    contentbody: '',
    emailcc: '',
    emailbcc: '',
    showjsonreport: false
});

function EmailConfigViewModel() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [emailConfigs, setEmailConfigs] = useState<EmailConfigModel[]>()
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<EditForm>(emptyEditForm());
    const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
    const [createForm, setCreateForm] = useState<CreateForm>(emptyCreateForm());

    const fetchEmailConfig = async () => {
        setIsLoading(true);
        const element = document.getElementsByClassName('Loader__background');

        try {
            if (element.length > 0) {
                element[0].className = 'Loader__background show';
            }
            const response = await axios.get<ResponseList<EmailConfigModel>>('/v1/email-config', {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                        'WF_APPLICATION'
                    )}`
                }
            });

            // console.log(response.data.result_list);

            if (element.length > 0) {
                element[0].className = 'Loader__background hide';
                setEmailConfigs(response.data.result_list)
            }
        } catch (error) {
            if (!error) return;

            if (error instanceof AxiosError) {
                Swal.fire(
                    'Error',
                    error.response?.data.message || 'ไม่ทราบสาเหตุ'
                );
            } else if (error instanceof Error) {
                Swal.fire('Error', error.message);
            }
        } finally {
            if (element.length > 0) {
                setIsLoading(false);
                element[0].className = 'Loader__background hide';
            }
        }
    };

    const handleStartEdit = (index: number) => {
        const item = emailConfigs![index];
        setEditingIndex(index);
        setEditForm({
            wftype: item.wftype ?? '',
            contentsubject: item.contentsubject ?? '',
            contentbody: item.contentbody ?? '',
            emailcc: item.emailcc ?? '',
            emailbcc: item.emailbcc ?? '',
            showjsonreport: item.showjsonreport === 'true'
        });
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditForm(emptyEditForm());
    };

    const handleSaveEdit = async () => {
        if (editingIndex === null || !emailConfigs) return;
        const item = emailConfigs[editingIndex];
        const element = document.getElementsByClassName('Loader__background');

        try {
            setIsLoading(true);
            if (element.length > 0) element[0].className = 'Loader__background show';

            await axios.put('/v1/email-config', {
                sid: item.sid,
                wftype: editForm.wftype,
                wfcondition: item.wfcondition,
                contentsubject: editForm.contentsubject,
                contentbody: editForm.contentbody,
                emailcc: editForm.emailcc,
                emailbcc: editForm.emailbcc,
                showjsonreport: editForm.showjsonreport ? 'true' : 'false',
                orig_sid: item.sid,
                orig_wftype: item.wftype,
                orig_wfcondition: item.wfcondition
            }, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('WF_APPLICATION')}`
                }
            });

            setEditingIndex(null);
            setEditForm(emptyEditForm());
            await fetchEmailConfig();
            Swal.fire('Success', 'บันทึกสำเร็จ', 'success');
        } catch (error) {
            if (error instanceof AxiosError) {
                Swal.fire('Error', error.response?.data.message || 'ไม่ทราบสาเหตุ');
            } else if (error instanceof Error) {
                Swal.fire('Error', error.message);
            }
        } finally {
            setIsLoading(false);
            if (element.length > 0) element[0].className = 'Loader__background hide';
        }
    };

    const handleOpenCreate = () => {
        setCreateForm(emptyCreateForm());
        setShowCreateForm(true);
    };

    const handleCancelCreate = () => {
        setShowCreateForm(false);
        setCreateForm(emptyCreateForm());
    };

    const handleSaveCreate = async () => {
        if (!createForm.sid || !createForm.wfcondition) {
            Swal.fire('Error', 'SID และ WF CONDITION จำเป็นต้องกรอก', 'error');
            return;
        }
        const element = document.getElementsByClassName('Loader__background');

        try {
            setIsLoading(true);
            if (element.length > 0) element[0].className = 'Loader__background show';

            await axios.post('/v1/email-config', {
                ...createForm,
                showjsonreport: createForm.showjsonreport ? 'true' : 'false'
            }, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('WF_APPLICATION')}`
                }
            });

            setShowCreateForm(false);
            setCreateForm(emptyCreateForm());
            await fetchEmailConfig();
            Swal.fire('Success', 'สร้างสำเร็จ', 'success');
        } catch (error) {
            if (error instanceof AxiosError) {
                Swal.fire('Error', error.response?.data.message || 'ไม่ทราบสาเหตุ');
            } else if (error instanceof Error) {
                Swal.fire('Error', error.message);
            }
        } finally {
            setIsLoading(false);
            if (element.length > 0) element[0].className = 'Loader__background hide';
        }
    };

    return {
        isLoading,
        emailConfigs,
        fetchEmailConfig,
        editingIndex,
        editForm,
        setEditForm,
        handleStartEdit,
        handleCancelEdit,
        handleSaveEdit,
        showCreateForm,
        createForm,
        setCreateForm,
        handleOpenCreate,
        handleCancelCreate,
        handleSaveCreate
    };
}

export default EmailConfigViewModel;
